import {
  Armchair,
  Baby,
  Bath,
  BedDouble,
  Briefcase,
  CookingPot,
  Flower2,
  Home,
  Shirt,
  UtensilsCrossed,
  Warehouse,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { getRoomKind } from "../../logic/rooms";
import type { RoomKind } from "../../logic/rooms";
import { cn } from "../../lib/utils";

interface RoomVisual {
  icon: LucideIcon;
  /** Pastel chip background + icon color, with dark-mode variants. */
  chip: string;
}

const ROOM_VISUALS: Record<RoomKind, RoomVisual> = {
  bedroom: {
    icon: BedDouble,
    chip: "bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300",
  },
  kids: {
    icon: Baby,
    chip: "bg-lime-100 text-lime-700 dark:bg-lime-500/15 dark:text-lime-300",
  },
  bathroom: {
    icon: Bath,
    chip: "bg-pink-100 text-pink-600 dark:bg-pink-500/15 dark:text-pink-300",
  },
  kitchen: {
    icon: CookingPot,
    chip: "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300",
  },
  dining: {
    icon: UtensilsCrossed,
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  },
  living: {
    icon: Armchair,
    chip: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300",
  },
  office: {
    icon: Briefcase,
    chip: "bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200",
  },
  laundry: {
    icon: Shirt,
    chip: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300",
  },
  garage: {
    icon: Warehouse,
    chip: "bg-stone-200 text-stone-700 dark:bg-stone-500/20 dark:text-stone-200",
  },
  outdoor: {
    icon: Flower2,
    chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  },
  other: {
    icon: Home,
    chip: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
  },
};

const getRoomVisual = (name: string | null | undefined): RoomVisual =>
  ROOM_VISUALS[getRoomKind(name)];

interface RoomIconProps {
  name: string | null | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZES = {
  sm: { box: "w-9 h-9 rounded-xl", icon: "w-[18px] h-[18px]" },
  md: { box: "w-12 h-12 rounded-2xl", icon: "w-6 h-6" },
  lg: { box: "w-16 h-16 rounded-3xl", icon: "w-8 h-8" },
} as const;

/** Pastel, color-coded icon chip for a room/space, chosen from its name. */
export function RoomIcon({ name, size = "md", className }: RoomIconProps) {
  const { icon: Icon, chip } = getRoomVisual(name);
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex items-center justify-center shrink-0",
        SIZES[size].box,
        chip,
        className
      )}
    >
      <Icon className={SIZES[size].icon} strokeWidth={2.2} />
    </span>
  );
}

export default RoomIcon;
